import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, type ComponentType } from 'react';
import PageMetadata from '@/components/PageMetadata';

// Import layouts (not lazy – always needed for shell)
import PublicLayout from '@/layouts/PublicLayout';
import AuthLayout from '@/layouts/AuthLayout';
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout';

// Eagerly loaded – critical path pages
import HomePage from '@/features/games/pages/HomePage';
import UserLoginPage from '@/features/auth/pages/UserLoginPage';

/** Lazy import + reload when a chunk fails (new deploy, stale cache) */
function lazyReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory().catch(() => {
      window.location.reload();
      return new Promise(() => {}) as Promise<{ default: T }>;
    })
  );
}

// Lazy-loaded pages – code-split for smaller initial bundle
const AccountHubPage = lazyReload(() => import('@/features/account/pages/AccountHubPage'));
const DashboardPage = lazyReload(() => import('@/features/account/pages/DashboardPage'));
const SettingsPage = lazyReload(() => import('@/features/account/pages/SettingsPage'));
const ChangePasswordPage = lazyReload(() => import('@/features/account/pages/ChangePasswordPage'));
const EmailPhonePage = lazyReload(() => import('@/features/account/pages/EmailPhonePage'));
const MessagesPage = lazyReload(() => import('@/features/account/pages/MessagesPage'));
const GamesPage = lazyReload(() => import('@/features/games/pages/GamesPage'));
const GamePlayPage = lazyReload(() => import('@/features/games/pages/GamePlayPage'));
const DepositPage = lazyReload(() => import('@/features/finance/pages/DepositPage'));
const PaymentCheckoutPage = lazyReload(() => import('@/features/finance/pages/PaymentCheckoutPage'));
const PayoutRequestPage = lazyReload(() => import('@/features/finance/pages/PayoutRequestPage'));
const WithdrawalLimitsLayout = lazyReload(() => import('@/features/finance/layouts/WithdrawalLimitsLayout'));
const WalletPage = lazyReload(() => import('@/features/finance/pages/WalletPage'));
const WalletDepositCheckoutPage = lazyReload(() => import('@/features/finance/pages/WalletDepositCheckoutPage'));
const WalletPayoutRequestPage = lazyReload(() => import('@/features/finance/pages/WalletPayoutRequestPage'));
const WithdrawalsPage = lazyReload(() => import('@/features/finance/pages/WithdrawalsPage'));
const TransactionsPage = lazyReload(() => import('@/features/finance/pages/TransactionsPage'));
const ActivatePage = lazyReload(() => import('@/features/auth/pages/ActivatePage'));
const ResetPasswordPage = lazyReload(() => import('@/features/auth/pages/ResetPasswordPage'));
const SupportPage = lazyReload(() => import('@/features/support/pages/SupportPage'));
const FAQPage = lazyReload(() => import('@/features/support/pages/FAQPage'));

// Minimal loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
  </div>
);

function AppRouter() {
  return (
    <>
      <PageMetadata
        title="Kiosk Gaming"
        description="Professional Gaming Kiosk Platform"
      />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
          </Route>

          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<UserLoginPage />} />
            <Route path="/activate" element={<ActivatePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Authenticated Routes - with Header and Footer */}
          <Route element={<AuthenticatedLayout />}>
            <Route path="/account" element={<AccountHubPage />} />
            <Route path="/account/dashboard" element={<DashboardPage />} />
            <Route path="/account/settings" element={<SettingsPage />} />
            <Route path="/account/change-password" element={<ChangePasswordPage />} />
            <Route path="/account/email-phone" element={<EmailPhonePage />} />
            <Route path="/account/messages" element={<MessagesPage />} />
            <Route path="/account/games" element={<GamesPage />} />
            <Route path="/account/finance/deposit" element={<DepositPage />} />
            <Route path="/account/finance/payment-checkout" element={<PaymentCheckoutPage />} />
            <Route path="/account/finance/payout-request" element={<PayoutRequestPage />} />
            <Route element={<WithdrawalLimitsLayout />}>
              <Route path="/account/finance/wallet" element={<WalletPage />} />
              <Route path="/account/wallet/payout-request" element={<WalletPayoutRequestPage />} />
            </Route>
            <Route path="/account/wallet/deposit-checkout" element={<WalletDepositCheckoutPage />} />
            <Route
              path="/account/finance/wallet/deposit"
              element={<Navigate to="/account/finance/wallet" replace />}
            />
            <Route
              path="/account/finance/wallet/withdraw"
              element={<Navigate to="/account/wallet/payout-request" replace />}
            />
            <Route path="/account/finance/withdrawals" element={<WithdrawalsPage />} />
            <Route path="/account/transactions" element={<TransactionsPage />} />
            {/* Authenticated ticket view */}
            <Route path="/account/support/tickets/:ticketNumber" element={<SupportPage />} />
          </Route>

          {/* Standalone Game Player (no app shell) */}
          <Route path="/account/games/play" element={<GamePlayPage />} />

          {/* Support Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/support" element={<SupportPage />} />
            <Route path="/support/tickets/:ticketNumber" element={<SupportPage />} />
            <Route path="/faq" element={<FAQPage />} />
          </Route>

          {/* Unknown paths → account hub */}
          <Route path="*" element={<Navigate to="/account" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default AppRouter;
